import React, { useEffect, useState } from "react";
import { Shield, Unlock, RefreshCw, Lock, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const OUTCOME_BADGES = {
    success: { Icon: CheckCircle2, color: "bg-green-100 text-green-700", label: "Success" },
    fail: { Icon: XCircle, color: "bg-red-100 text-red-700", label: "Failed" },
    locked: { Icon: AlertTriangle, color: "bg-orange-100 text-orange-700", label: "Locked" },
};

const fmtTime = (s) => {
    if (!s) return "—";
    try {
        return new Date(s).toLocaleString();
    } catch {
        return s;
    }
};

const SecurityPanel = () => {
    const [events, setEvents] = useState([]);
    const [lockouts, setLockouts] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [e, l] = await Promise.all([
                api.get("/admin/security/events", { params: { limit: 100 } }),
                api.get("/admin/security/lockouts"),
            ]);
            setEvents(e.data || []);
            setLockouts(l.data || []);
        } catch {
            toast.error("Failed to load security data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const unlock = async (identifier) => {
        try {
            const { data } = await api.post("/admin/security/unlock", { identifier });
            toast.success(`Cleared ${data.cleared} record(s) for ${identifier}`);
            load();
        } catch {
            toast.error("Failed to unlock");
        }
    };

    const activeLockouts = lockouts.filter((l) => l.is_locked);

    return (
        <div data-testid="admin-security-panel" className="space-y-8">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Shield className="w-6 h-6 text-[#E53935]" strokeWidth={1.5} />
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Security</h2>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="security-refresh-btn"
                    className="inline-flex items-center gap-2 text-sm font-medium text-[#1C252E] hover:text-[#E53935] disabled:opacity-50"
                    aria-label="Refresh security data"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
                    Refresh
                </button>
            </div>

            {/* Active lockouts */}
            <div className="bg-white border border-black/5 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Lock className="w-4 h-4 text-[#E53935]" strokeWidth={1.5} />
                    <h3 className="font-heading text-lg text-[#1C252E] font-medium">
                        Active lockouts ({activeLockouts.length})
                    </h3>
                </div>
                {activeLockouts.length === 0 ? (
                    <p data-testid="security-no-lockouts" className="text-sm text-[#4A5568]">
                        No accounts are currently locked. Brute-force defense is healthy.
                    </p>
                ) : (
                    <ul className="space-y-3">
                        {activeLockouts.map((l) => (
                            <li
                                key={l.identifier}
                                data-testid={`lockout-row-${l.identifier}`}
                                className="flex items-center justify-between gap-4 p-3 bg-red-50 border border-red-100 rounded-xl"
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="font-mono text-sm text-[#1C252E] truncate">{l.identifier}</p>
                                    <p className="text-xs text-[#4A5568] mt-0.5">
                                        Locked until {fmtTime(l.locked_until)} · {l.fails} recent fails
                                    </p>
                                </div>
                                <button
                                    onClick={() => unlock(l.identifier)}
                                    data-testid={`unlock-btn-${l.identifier}`}
                                    className="inline-flex items-center gap-1.5 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-4 py-2 text-xs font-medium transition-all"
                                >
                                    <Unlock className="w-3.5 h-3.5" strokeWidth={2} />
                                    Unlock now
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Recent activity log */}
            <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                <div className="px-6 py-4 border-b border-black/5">
                    <h3 className="font-heading text-lg text-[#1C252E] font-medium">
                        Recent login activity ({events.length})
                    </h3>
                    <p className="text-xs text-[#4A5568] mt-1">
                        Auto-purged after 30 days. Shows successes, failures, and lockout-blocked attempts.
                    </p>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                            <tr>
                                <th className="text-left px-6 py-3 font-medium">When</th>
                                <th className="text-left px-4 py-3 font-medium">Outcome</th>
                                <th className="text-left px-4 py-3 font-medium">Email</th>
                                <th className="text-left px-4 py-3 font-medium">IP</th>
                                <th className="text-left px-4 py-3 font-medium">Reason</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5" data-testid="security-events-table">
                            {events.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-6 py-10 text-center text-[#4A5568]">
                                        No login activity recorded yet.
                                    </td>
                                </tr>
                            )}
                            {events.map((ev, i) => {
                                const b = OUTCOME_BADGES[ev.outcome] || OUTCOME_BADGES.fail;
                                return (
                                    <tr key={i} data-testid={`security-event-row-${i}`}>
                                        <td className="px-6 py-3 text-[#4A5568] whitespace-nowrap">{fmtTime(ev.at)}</td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${b.color}`}>
                                                <b.Icon className="w-3 h-3" strokeWidth={2} />
                                                {b.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-[#1C252E] font-medium truncate max-w-[200px]">{ev.email}</td>
                                        <td className="px-4 py-3 font-mono text-xs text-[#4A5568]">{ev.ip}</td>
                                        <td className="px-4 py-3 text-xs text-[#4A5568] truncate max-w-[200px]">{ev.reason || "—"}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SecurityPanel;
